import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { FiShoppingBag } from 'react-icons/fi';
import { auth, db } from '../../firebase/firebase';
import PortalLoginShell from '../../components/shared/PortalLoginShell';

const VendorLoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const email = (user.email || '').toLowerCase();

      if (!email) {
        await signOut(auth);
        setError('Could not read email from Google account.');
        return;
      }

      const whitelistRef = doc(db, 'vendorAdmins', email);
      const whitelistSnap = await getDoc(whitelistRef);
      if (!whitelistSnap.exists()) {
        await signOut(auth);
        setError(`${email} is not authorized as a vendor. Contact your platform administrator.`);
        return;
      }

      const whitelist = whitelistSnap.data();
      const vendorRef = doc(db, 'vendors', user.uid);
      const vendorSnap = await getDoc(vendorRef);

      if (!vendorSnap.exists()) {
        await setDoc(vendorRef, {
          email,
          businessName: whitelist.businessName || '',
          name: whitelist.businessName || '',
          isActive: true,
          createdAt: new Date(),
          authUid: user.uid,
        });
      }

      if (!whitelist.vendorId || whitelist.status !== 'active') {
        await updateDoc(whitelistRef, {
          vendorId: user.uid,
          status: 'active',
          firstLoginAt: whitelist.firstLoginAt || new Date(),
        });
      }

      navigate('/vendor/dashboard');
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        setError('');
      } else {
        console.error('Vendor sign-in error:', err);
        setError(err.message || 'Sign-in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PortalLoginShell
      portalLabel="Vendor Portal"
      title="Get your offers in front of every Loysta employee."
      description="Create offers, track redemptions, and scan QR codes at checkout — all from your vendor portal."
      accent="#8b5cf6"
      Icon={FiShoppingBag}
      onGoogleSignIn={handleGoogleSignIn}
      loading={loading}
      error={error}
      footerHint="Sign in with the Google account your platform administrator whitelisted as your vendor account."
      otherPortals={[
        { to: '/admin/login', label: 'Super Admin' },
        { to: '/company/login', label: 'Company login' },
      ]}
    />
  );
};

export default VendorLoginPage;
